<div class="table-container">
  <ng-container *ngIf="isTableLoading">
    <sh-spinner
      overlay
      label="{{ 'syngovia.label.loading' | translate }}"
      class="table-loader-spinner"
    ></sh-spinner>
  </ng-container>
  <div class="table-container__description flex-row flex--jc-sb flex--wrap">
    <div class="flex-row">
      <img
        [src]="dnaSyngoviaIconService.getImgIcon(groupNode.nodes[0].type).src"
        [alt]="dnaSyngoviaIconService.getImgIcon(groupNode.nodes[0].type).alt"
      />
      <div class="description">
        {{ groupNode.description }} ({{ groupNode.nodes.length }})
      </div>
      <sh-button
        *ngIf="
          !(
            (screenLayoutService.phone$ | async) ||
            (screenLayoutService.tablet$ | async)
          )
        "
        label="{{ 'syngovia.label.compare' | translate }}"
        (click)="onCompareClick()"
        onkeyup="onCompareClick()"
      ></sh-button>
    </div>

    <div class="flex-row search-container">
      <sh-input-text
        #searchInput
        (value-changed)="onInputChange(searchInput.value)"
        label="{{ 'syngovia.label.search' | translate }}"
      >
        <sh-icon icon="search" size="s" slot="icon"></sh-icon>
      </sh-input-text>
    </div>
  </div>

  <div class="table-wrapper">
    <sh-table
      multiselect
      [class.table-fixed-height]="filteredNodes.length > 10"
    >
      <!-- table headers -->
      <ng-container
        *ngTemplateOutlet="
          computedHeader;
          context: {
            $implicit: {
              headers:
                columnsDictionary[groupNode.type] ?? columnsDictionary.default,
              nested: true,
            },
          }
        "
      ></ng-container>

      <!-- table rows -->
      <ng-container
        *ngTemplateOutlet="
          computedRows;
          context: {
            $implicit: {
              columns:
                columnsDictionary[groupNode.type] ?? columnsDictionary.default,
              nodes: filteredNodes,
              groupNodeType: groupNode.type,
            },
          }
        "
      ></ng-container>
    </sh-table>
  </div>
</div>

<ng-template #computedHeader let-data>
  <sh-table-row slot="header">
    <!-- warning and checkbox column -->
    <sh-table-head checkbox unsortable>
      <div class="icons-wrapper">
        <sh-icon icon=""></sh-icon>
        <sh-checkbox
          label=""
          [active]="
            selectionModel.isAllSelected() ||
            this.selectionModel.isIndeterminate()
          "
          [indeterminate]="this.selectionModel.isIndeterminate()"
          (checked)="
            onHeaderCheckboxClick($event.target.hasAttribute('active'))
          "
        ></sh-checkbox>
      </div>
    </sh-table-head>

    <!-- other columns -->
    <sh-table-head
      *ngFor="let header of data.headers"
      [columns]="header.columnSize"
      [attr.min-width]="header.minWidth"
      (toggle)="
        sortByProperty(
          header.property,
          $event.target.hasAttribute('sort-descending')
        )
      "
      [attr.sort-descending]="false"
      sorted
      [class.syngovia-nested]="data.nested"
    >
      {{ header.label | translate }}
    </sh-table-head>
  </sh-table-row>
</ng-template>

<ng-template #computedRows let-data>
  <sh-table-row *ngFor="let node of data.nodes">
    <!-- warning and checkbox column -->
    <sh-table-cell checkbox>
      <div class="icons-wrapper">
        <div class="warning-icon-wrapper">
          <sh-icon
            *ngIf="node.value?.warnings?.length > 0"
            icon="warning"
            size="s"
            [class.pointer]="node.value.type === 'server'"
            (click)="onServerWarningClick(node.value)"
            onkeyup="onServerWarningClick(node.value)"
          ></sh-icon>
        </div>
        <sh-checkbox
          label=""
          [active]="node.selected"
          (checked)="onRowCheckboxClick(node)"
        ></sh-checkbox>
      </div>
    </sh-table-cell>

    <!-- other columns -->
    <ng-container
      *ngTemplateOutlet="
        rowCell;
        context: { $implicit: { columns: data.columns, node: node.value } }
      "
    ></ng-container>

    <ng-container
      *ngIf="
        ['modality', 'digital_product', 'dicom_proxy'].includes(
          data.groupNodeType
        )
      "
    >
      <!-- Dicom services -->
      <ng-container *ngIf="node.value?.services?.length > 0">
        <ng-container
          *ngTemplateOutlet="
            nestedRowWithNoChild;
            context: {
              $implicit: {
                columns: nodeDicomServiceColumns,
                values: node.value.services,
              },
            }
          "
        ></ng-container>
      </ng-container>
    </ng-container>

    <ng-container *ngIf="data.groupNodeType === 'server'">
      <!-- Server network adapters -->
      <ng-container *ngIf="node.value?.networkAdapters?.length > 0">
        <ng-container
          *ngTemplateOutlet="
            nestedRowWithNoChild;
            context: {
              $implicit: {
                columns: serverNetworkAdaptersColumns,
                values: node.value.networkAdapters,
              },
            }
          "
        ></ng-container>
      </ng-container>
    </ng-container>

    <ng-container *ngIf="data.groupNodeType === 'client'">
      <!-- Client network adapters -->
      <ng-container *ngIf="node.value?.networkAdapters?.length > 0">
        <ng-container
          *ngTemplateOutlet="
            nestedRowWithNoChild;
            context: {
              $implicit: {
                columns: clientNetworkAdaptersColumns,
                values: node.value.networkAdapters,
              },
            }
          "
        ></ng-container>
      </ng-container>

      <!-- Client video Controllers -->
      <ng-container *ngIf="node.value?.videoControllers?.length > 0">
        <ng-container
          *ngTemplateOutlet="
            nestedRowWithChild;
            context: {
              $implicit: {
                columns: clientVideoControllersColumns,
                values: node.value.videoControllers,
              },
            }
          "
        ></ng-container>
      </ng-container>
    </ng-container>
  </sh-table-row>
</ng-template>

<ng-template #nestedRowWithChild let-data>
  <sh-table-row
    *ngFor="let node of data.values; let first = first"
    class="syngovia-nested"
  >
    <ng-container
      *ngTemplateOutlet="
        nestedRowCell;
        context: {
          $implicit: { columns: data.columns, node, first },
        }
      "
    ></ng-container>

    <!-- Client display Controllers -->
    <ng-container *ngIf="node.displayControllers?.length > 0">
      <ng-container
        *ngTemplateOutlet="
          nestedRowWithNoChild;
          context: {
            $implicit: {
              columns: clientDisplayControllersColumns,
              values: node.displayControllers,
            },
          }
        "
      ></ng-container>
    </ng-container>
  </sh-table-row>
</ng-template>

<ng-template #nestedRowWithNoChild let-data>
  <sh-table-row
    *ngFor="let node of data.values; let first = first"
    class="syngovia-nested"
  >
    <ng-container
      *ngTemplateOutlet="
        nestedRowCell;
        context: {
          $implicit: { columns: data.columns, node, first },
        }
      "
    ></ng-container>
  </sh-table-row>
</ng-template>

<ng-template #rowCell let-data>
  <sh-table-cell
    *ngFor="let column of data.columns"
    [columns]="column.columnSize"
    [attr.min-width]="column.minWidth"
  >
    <ng-container *ngIf="column.columnType === 'string'">
      {{ data.node[column.property] ?? ' - ' }}
    </ng-container>

    <ng-container *ngIf="column.columnType === 'date'">
      {{ data.node[column.property] | localizedDate }}
    </ng-container>
  </sh-table-cell>
</ng-template>

<ng-template #nestedRowCell let-data>
  <sh-table-cell
    *ngFor="let column of data.columns"
    [columns]="column.columnSize"
    [attr.min-width]="column.minWidth"
  >
    <div class="nested-header-cell">
      <div
        *ngIf="data.first"
        [ngClass]="{ 'nested-header-cell--line-height': data.first }"
      >
        {{ column.label | translate }}
      </div>

      <div [ngClass]="{ 'nested-header-cell--line-height': data.first }">
        <ng-container *ngIf="column.columnType === 'string'">
          {{ data.node[column.property] ?? ' - ' }}
        </ng-container>

        <ng-container *ngIf="column.columnType === 'date'">
          {{ data.node[column.property] | localizedDate }}
        </ng-container>
      </div>
    </div>
  </sh-table-cell>
</ng-template>

<ng-container *ngIf="modalVisible">
  <sh-modal
    sticky
    [visible]="modalVisible"
    id="comparisons-modal"
    label="{{ 'syngovia.label.compareDevices' | translate }}"
    modal-width="64.5rem"
    modal-height="calc(100vh - 0.625rem)"
  >
    <div slot="header" class="modal-header">
      <sh-text size="header-2">
        {{ 'syngovia.label.compareDevices' | translate }}
      </sh-text>
      <sh-button
        color="tertiary"
        label="{{ 'syngovia.label.clearAll' | translate }}"
        (click)="board.clearAllNodes()"
        onkeyup="board.clearAllNodes()"
      ></sh-button>
    </div>

    <app-dna-syngovia-comparison-board
      #board
      [groupNode]="groupNode"
      [selectedNodes]="selectionModel.selectedValues()"
    ></app-dna-syngovia-comparison-board>

    <sh-button
      slot="footer"
      label="{{ 'syngovia.label.close' | translate }}"
      size="m"
      (click)="onCloseCompareModalClick()"
      onkeyup="onCloseCompareModalClick()"
    ></sh-button>
  </sh-modal>
</ng-container>
